import { ApolloError } from "@apollo/client";

/**
 * Recognises the error shapes a bulk add confirm produces when it runs out of time while PCS keeps
 * adding users in the background. PCS adds users one at a time and nothing cancels that work, so a
 * timeout does not mean nothing was added; the page must not present it as an ordinary failure.
 *
 * The FM UI backend's API Gateway rewrites every 5xx to 500 (FITManagementUIBackendServiceCDK
 * lib/stacks/apigateway/apigateway.ts, DEFAULT_5XX), so the status code alone cannot tell a timeout
 * from a real server error. Each shape below is matched on its body, not its status.
 */

/** AmazonGQLServerJavaCore GraphQLExecutionTimeoutError classification. */
const AGQL_TIMEOUT_CLASSIFICATION = "ExecutionAborted";
/** AmazonGQLServerJavaCore GraphQLExecutionTimeoutError message: "...unable to finish in a limit of 10000 millis". */
const AGQL_TIMEOUT_MESSAGE = "unable to finish in a limit of";
/** API Gateway's integration-timeout body: {"message": "Endpoint request timed out"}. */
const GATEWAY_TIMEOUT_MESSAGE = "Endpoint request timed out";
/** The FM UI backend's error when its PCS call gets the PCS API Gateway's 504 at 29 s. */
const PCS_504_MESSAGE = "Received 504 response";
/** 500 is what the browser sees after the DEFAULT_5XX rewrite; 504 is accepted in case that rewrite is removed. */
const GATEWAY_TIMEOUT_STATUS_CODES = [500, 504];

type NetworkErrorWithBody = {
    statusCode?: number;
    result?: unknown;
    bodyText?: string;
};

const bodyAsText = (body: unknown): string => {
    if (body === undefined || body === null) return "";
    if (typeof body === "string") return body;
    try {
        return JSON.stringify(body);
    } catch {
        return "";
    }
};

const isGraphQLTimeout = (error: ApolloError): boolean =>
    (error.graphQLErrors ?? []).some(e => {
        const message = e?.message ?? "";
        return e?.extensions?.classification === AGQL_TIMEOUT_CLASSIFICATION
            || message.includes(AGQL_TIMEOUT_MESSAGE)
            || message.includes(PCS_504_MESSAGE);
    });

const isGatewayTimeout = (error: ApolloError): boolean => {
    const networkError = error.networkError as NetworkErrorWithBody | null | undefined;
    if (!networkError || !GATEWAY_TIMEOUT_STATUS_CODES.includes(networkError.statusCode ?? -1)) {
        return false;
    }
    const body = bodyAsText(networkError.result) + bodyAsText(networkError.bodyText);
    return body.includes(GATEWAY_TIMEOUT_MESSAGE);
};

/**
 * True when a bulk add confirm failed because a request timed out, in any of three shapes:
 * - AGQL's 10 s execution timeout: HTTP 200 with an ExecutionAborted GraphQL error.
 * - The FM UI backend's API Gateway integration timeout: HTTP 500 (or 504) whose body is
 *   "Endpoint request timed out". A 500 without that body is a real error.
 * - The backend's PCS call receiving PCS's 504: a GraphQL error containing "Received 504 response".
 */
export const isBulkAddTimeout = (error: ApolloError | undefined | null): boolean =>
    !!error && (isGraphQLTimeout(error) || isGatewayTimeout(error));
